import type { CaptureResult, PostHog } from 'posthog-js/dist/module.no-external'
import { analyticsAllowed, subscribeAnalyticsConsent } from './analytics-consent'
import { publicTrafficSurface } from './public-traffic'
import type { SeaaBootstrap } from './app-runtime'

const sensitivePropertyName = /email|phone|token|booking.?code|customer.?name/i
const navigationPropertyName = /(?:^|_)(?:url|pathname|referrer|href|filename|abs.?path)$/i

function redactCapabilityReferences(value: string) {
  return value
    .replace(/\/api\/public\/bookings\/manage\/[^/?#\s]+/g, '/api/public/bookings/manage/[redacted]')
    .replace(/\/api\/public\/bookings\/(?!manage(?:\/|$)|lookup(?:\/|$))[^/?#\s]+/g, '/api/public/bookings/[redacted]')
    .replace(/\/api\/public\/feedback\/[^/?#\s]+/g, '/api/public/feedback/[redacted]')
    .replace(/\/api\/public\/waitlist\/offers\/[^/?#\s]+/g, '/api/public/waitlist/offers/[redacted]')
    .replace(/\/team\/invite\/[^/?#\s]+/g, '/team/invite')
    .replace(/\/(manage|waitlist|confirmation)\/[^/?#\s]+/g, '/$1/[redacted]')
    .replace(/([?&#](?:token|invite|hold|feedback|code)=)[^&#\s]+/gi, '$1[redacted]')
}

export function publicObservedUrl(value: unknown) {
  if (typeof value !== 'string') return value
  try {
    const url = new URL(value, window.location.origin)
    const pathname = redactCapabilityReferences(url.pathname)
    return `${url.origin}${pathname}`
  } catch {
    return redactCapabilityReferences(value)
  }
}

const invitationStorageKey = 'seaa_staff_invitation_token'

export function bootstrapStaffInvitationToken() {
  if (typeof window === 'undefined') return
  const legacyMatch = window.location.pathname.match(/^\/team\/invite\/([A-Za-z0-9_-]{40,128})$/)
  const fragment = new URLSearchParams(window.location.hash.slice(1)).get('invite')
  const token = legacyMatch?.[1] ?? fragment
  if (token && /^[A-Za-z0-9_-]{40,128}$/.test(token)) {
    window.sessionStorage.setItem(invitationStorageKey, token)
    window.history.replaceState(window.history.state, '', '/team/invite')
  }
}

export function readStaffInvitationToken() {
  if (typeof window === 'undefined') return ''
  return window.sessionStorage.getItem(invitationStorageKey) ?? ''
}

export function clearStaffInvitationToken() {
  if (typeof window === 'undefined') return
  window.sessionStorage.removeItem(invitationStorageKey)
}

function sanitizeObservedValue(value: unknown, propertyName = '', path: string[] = []): unknown {
  if (typeof value === 'string') {
    return navigationPropertyName.test(propertyName)
      ? publicObservedUrl(value)
      : redactCapabilityReferences(value)
  }
  if (Array.isArray(value)) return value.map((entry) => sanitizeObservedValue(entry, '', path))
  if (!value || typeof value !== 'object') return value

  const sanitized: Record<string, unknown> = {}
  for (const [key, nestedValue] of Object.entries(value)) {
    const isPostHogProjectToken = key === 'token' && path.length === 1 && path[0] === 'properties'
    if (sensitivePropertyName.test(key) && !isPostHogProjectToken) continue
    sanitized[key] = sanitizeObservedValue(nestedValue, key, [...path, key])
  }
  return sanitized
}

export function sanitizeObservedEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) return null
  return sanitizeObservedValue(event, '', []) as CaptureResult
}

let observabilityClient: PostHog | undefined
let starting: Promise<void> | undefined
let consentListenerInstalled = false
let observabilityEnabled = false
let previousPageview = ''
type ObservabilityContext = Pick<SeaaBootstrap, 'origin' | 'customerAppOrigin' | 'site' | 'locale'>
let observabilityContext: ObservabilityContext | undefined
function publicAnalyticsAllowed() {
  if (!import.meta.env.PROD || import.meta.env.MODE === 'test') return false
  if (typeof window === 'undefined' || !observabilityContext || !analyticsAllowed()) return false
  if (/^(localhost|127(?:\.\d+){3}|\[::1\])$|\.localhost$/.test(window.location.hostname)) return false
  const customerOrigin = observabilityContext.customerAppOrigin || observabilityContext.origin
  if (window.location.origin !== customerOrigin && !observabilityContext.site) return false
  return publicTrafficSurface(window.location.pathname, Boolean(observabilityContext.site)) !== null
}
function capturePublicPageview() {
  if (!observabilityEnabled || !publicAnalyticsAllowed()) return
  const requestedLanguage = new URLSearchParams(window.location.search).get('lang')
  const language = requestedLanguage === 'en' || requestedLanguage === 'ar' ? requestedLanguage : observabilityContext?.locale
  const navigation = `${window.location.pathname}:${language}`
  if (navigation === previousPageview) return
  previousPageview = navigation
  observabilityClient?.capture('$pageview', { service: 'seaa-web', environment: 'production', language })
}
export async function startWebObservability(bootstrap?: ObservabilityContext) {
  if (bootstrap) observabilityContext = bootstrap
  if (!consentListenerInstalled) {
    consentListenerInstalled = true
    subscribeAnalyticsConsent(() => { void startWebObservability() })
  }
  const key = import.meta.env.VITE_POSTHOG_KEY?.trim()
  if (!publicAnalyticsAllowed()) {
    if (observabilityEnabled) {
      observabilityClient?.opt_out_capturing()
      observabilityClient?.reset(true)
    }
    observabilityEnabled = false
    previousPageview = ''
    return
  }
  if (!key || import.meta.env.MODE === 'test') return
  if (observabilityClient) {
    if (!observabilityEnabled) observabilityClient.opt_in_capturing({ captureEventName: false })
    observabilityEnabled = true
    capturePublicPageview()
    return
  }
  if (starting) return starting
  starting = initializeObservability(key).catch(() => { /* Optional analytics must not block the public journey. */ }).finally(() => { starting = undefined })
  return starting
}
async function initializeObservability(key: string) {
  await import('posthog-js/dist/exception-autocapture')
  const { default: posthog } = await import('posthog-js/dist/module.no-external')
  if (!publicAnalyticsAllowed()) return
  observabilityClient = posthog
  posthog.init(key, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com',
    defaults: '2026-05-30',
    person_profiles: 'identified_only',
    autocapture: false,
    // Router resolution and consent restoration share one deduplicated path.
    // Automatic history capture runs before a previously opted-out SDK resumes.
    capture_pageview: false,
    capture_pageleave: true,
    capture_exceptions: true,
    // Caddy permits only first-party scripts; bundle extensions with the app.
    disable_external_dependency_loading: true,
    // Seaa uses no client flags. Prevent flag requests during identity resets.
    advanced_disable_flags: true,
    disable_session_recording: true,
    persistence: 'memory',
    opt_out_capturing_persistence_type: 'cookie',
    consent_persistence_name: 'seaa_posthog_consent',
    cookie_expiration: 180,
    cross_subdomain_cookie: false,
    mask_all_text: true,
    mask_all_element_attributes: true,
    before_send: (event) => publicAnalyticsAllowed() ? sanitizeObservedEvent(event) : null,
    loaded: (client) => client.register({ service: 'seaa-web', environment: 'production' }),
  })
  posthog.opt_in_capturing({ captureEventName: false })
  observabilityEnabled = true
  capturePublicPageview()
}
